"use client";
import { Box, Circle, HStack, SimpleGrid, Stack } from "@chakra-ui/react";
import Image from "next/image";
import { Balancer } from "react-wrap-balancer";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";
import { Heading, Text } from "@/elements";
import RedCross from "public/shared/redcross.png";
import GreenTick from "public/shared/greenCheck.png";
import BgImage from "public/shared/lbBg.png";
import PageLayout from "@/layouts/pageLayout";

type ComparisonItemProps = {
  title: string;
  desc: string;
  items: {
    title: string;
    subTitle: string;
    points: string[];
  }[];
};

const TraditionalComparison = (props: ComparisonItemProps) => {
  const { title, desc, items } = props;
  const headingVariant = useHeadingVariant();
  const crossCircleBg = "#FEE2E2";
  const tickCircleBg = "#D1FAE5";
  return (
    <Box id="comparison" bgImage={`url(${BgImage.src})`} bgSize="cover" py={10} mt={16}>
      <PageLayout spacing={12} marginTop={0}>
        <Heading as="h2" variant={headingVariant} textAlign={"center"}>
          {title}
        </Heading>
        <Balancer>
          <Text
            variant={"label1"}
            textAlign="center"
            color="text.100"
            maxW="5xl"
            mx="auto"
          >
            {desc}
          </Text>
        </Balancer>
        <SimpleGrid
          columns={{ base: 1, sm: 2 }}
          gap={{ base: 8, md: 12, "2xl": 16 }}
          px={{ base: 3, sm: 2, lg: 16, xl: 28, "2xl": 48 }}
        >
          {items.map((item, index) => (
            <Box
              key={index}
              bg={"white"}
              borderRadius="2xl"
              p={{ base: 4, sm: 6, md: 8 }}
              shadow="lg"
              transition="all 0.3s ease"
              _hover={{ transform: "translateY(-4px)", shadow: "xl" }}
            >
              <Stack
                direction={{ base: "column", md: "row" }}
                gap={{ base: 4, md: 4, xl: 8 }}
              >
                <Text variant={"label1"} textAlign={"center"}>
                  {item.title}
                </Text>
                <Text variant={"label1"} color={"text.100"} fontWeight={600}>
                  {item.subTitle}
                </Text>
              </Stack>
              <Stack gap={4} mt={4} color="text.100">
                {item.points.map((point, pointIdx) => (
                  <HStack key={pointIdx} gap={2}>
                    <Circle
                      size="32px"
                      bgColor={index % 2 === 0 ? crossCircleBg : tickCircleBg}
                    >
                      <Image
                        alt={"Point"}
                        src={index % 2 === 0 ? RedCross : GreenTick}
                        width={20}
                        height={20}
                      />{" "}
                    </Circle>
                    <Text variant={"body2"}>{point}</Text>
                  </HStack>
                ))}
              </Stack>
            </Box>
          ))}
        </SimpleGrid>
      </PageLayout>
    </Box>
  );
};

export default TraditionalComparison;
